/**
 * PREMIUM VARIANT SLIDER
 * Premium fashion ecommerce variant slider with Swiper.js
 * Supports color swatches and size options with responsive design
 */

(function() {
  'use strict';

  // Track initialized sliders
  const initializedSliders = new WeakSet();

  // Configuration
  const CONFIG = {
    desktop: {
      spaceBetween: 8,
      slidesPerView: 'auto',
    },
    tablet: {
      spaceBetween: 7,
      slidesPerView: 'auto',
    },
    mobile: {
      spaceBetween: 6,
      slidesPerView: 'auto',
    }
  };

  /**
   * Initialize variant slider
   */
  function initVariantSlider(sliderElement) {
    // Prevent double initialization using WeakSet
    if (initializedSliders.has(sliderElement)) {
      return sliderElement.swiper;
    }

    // Also check if swiper property exists
    if (sliderElement.swiper) {
      initializedSliders.add(sliderElement);
      return sliderElement.swiper;
    }

    const wrapper = sliderElement.closest('.variant-slider-wrap');
    if (!wrapper) {
      console.warn('Variant slider: No wrapper found for', sliderElement);
      return;
    }

    const prevButton = wrapper.querySelector('.variant-prev');
    const nextButton = wrapper.querySelector('.variant-next');

    if (!prevButton || !nextButton) {
      console.warn('Variant slider: Navigation buttons not found for', sliderElement);
    }

    // Initialize Swiper
    const swiper = new Swiper(sliderElement, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      freeMode: false,
      grabCursor: true,
      threshold: 5,
      resistance: true,
      resistanceRatio: 0.85,
      watchOverflow: true,
      
      navigation: {
        nextEl: nextButton,
        prevEl: prevButton,
      },

      breakpoints: {
        320: {
          spaceBetween: CONFIG.mobile.spaceBetween,
        },
        768: {
          spaceBetween: CONFIG.tablet.spaceBetween,
        },
        1025: {
          spaceBetween: CONFIG.desktop.spaceBetween,
        }
      },

      // Smooth transitions
      speed: 300,
      
      // Events
      on: {
        init: function() {
          updateNavigationVisibility(this, prevButton, nextButton);
          initializedSliders.add(sliderElement);
        },
        resize: function() {
          updateNavigationVisibility(this, prevButton, nextButton);
        },
        slideChange: function() {
          updateNavigationVisibility(this, prevButton, nextButton);
        }
      }
    });

    // Handle window resize with debounce
    let resizeTimer;
    const resizeHandler = function() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function() {
        if (swiper && !swiper.destroyed) {
          swiper.update();
          updateNavigationVisibility(swiper, prevButton, nextButton);
        }
      }, 250);
    };
    
    window.addEventListener('resize', resizeHandler);

    // Store cleanup function
    sliderElement._cleanup = function() {
      window.removeEventListener('resize', resizeHandler);
      if (swiper && !swiper.destroyed) {
        swiper.destroy(true, true);
      }
      initializedSliders.delete(sliderElement);
    };

    return swiper;
  }

  /**
   * Update navigation button visibility
   */
  function updateNavigationVisibility(swiper, prevButton, nextButton) {
    if (!swiper || !prevButton || !nextButton) return;

    const wrapper = swiper.wrapperEl;
    const container = swiper.el;
    
    if (!wrapper || !container) return;

    // Calculate if content overflows
    const wrapperWidth = wrapper.scrollWidth;
    const containerWidth = container.clientWidth;
    const shouldShowNav = wrapperWidth > containerWidth + 5; // 5px buffer

    requestAnimationFrame(function() {
      if (shouldShowNav) {
        prevButton.style.display = 'flex';
        nextButton.style.display = 'flex';
        
        // Update disabled state based on position
        if (swiper.isBeginning) {
          prevButton.classList.add('swiper-button-disabled');
        } else {
          prevButton.classList.remove('swiper-button-disabled');
        }
        
        if (swiper.isEnd) {
          nextButton.classList.add('swiper-button-disabled');
        } else {
          nextButton.classList.remove('swiper-button-disabled');
        }
      } else {
        prevButton.style.display = 'none';
        nextButton.style.display = 'none';
      }
    });
  }

  /**
   * Initialize all variant sliders on the page
   */
  function initAllSliders() {
    const sliders = document.querySelectorAll('.variant-swiper');
    
    sliders.forEach(function(slider) {
      // Small delay to ensure DOM is ready
      setTimeout(function() {
        initVariantSlider(slider);
      }, 50);
    });
  }

  /**
   * Observe for dynamically added sliders
   */
  function observeDynamicSliders() {
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) { // Element node
            // Check if the added node is a slider
            if (node.classList && node.classList.contains('variant-swiper')) {
              setTimeout(function() {
                initVariantSlider(node);
              }, 100);
            }
            // Check if the added node contains sliders
            const sliders = node.querySelectorAll && node.querySelectorAll('.variant-swiper');
            if (sliders && sliders.length > 0) {
              sliders.forEach(function(slider) {
                setTimeout(function() {
                  initVariantSlider(slider);
                }, 100);
              });
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return observer;
  }

  /**
   * Handle variant selection
   */
  function handleVariantSelection() {
    document.addEventListener('change', function(e) {
      const input = e.target;
      
      if (input.type === 'radio' && input.closest('.variant-swiper')) {
        const label = input.nextElementSibling;
        
        // Add selection animation
        if (label) {
          label.style.transition = 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)';
        }

        // Scroll to selected item if needed
        const swiperEl = input.closest('.variant-swiper');
        if (swiperEl && swiperEl.swiper) {
          const slide = input.closest('.swiper-slide');
          if (slide) {
            const slideIndex = Array.from(slide.parentNode.children).indexOf(slide);
            swiperEl.swiper.slideTo(slideIndex, 300);
          }
        }
      }
    });
  }

  /**
   * Cleanup function for page unload
   */
  function cleanup() {
    document.querySelectorAll('.variant-swiper').forEach(function(slider) {
      if (slider._cleanup) {
        slider._cleanup();
      }
    });
  }

  /**
   * Initialize on DOM ready
   */
  function initialize() {
    initAllSliders();
    observeDynamicSliders();
    handleVariantSelection();
    
    // Cleanup on page unload
    window.addEventListener('beforeunload', cleanup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    // DOM already loaded
    initialize();
  }

  /**
   * Reinitialize on Shopify section load (for theme editor)
   */
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function() {
      setTimeout(initAllSliders, 100);
    });
    
    document.addEventListener('shopify:section:unload', cleanup);
  }

  /**
   * Expose init function globally for manual initialization
   */
  window.initVariantSlider = initVariantSlider;
  window.initAllVariantSliders = initAllSliders;

})();
